import type { ClientMediaItem } from '@DigitaleDoerfer/digitale-doerfer-api';
import type { FC } from 'react';
import { useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import { ActivityIndicator, Avatar, IconButton } from 'react-native-paper';

import { AddAPhoto, EditIcon } from '@/assets';
import { MediaItemImage } from '@/features/media/components/MediaItemImage';
import type { AvatarTextFallback } from '@/features/media/types/avatar';
import { MediaItemSize } from '@/features/media/types/mediaItem';
import { circleSize } from '@/shared/utils/circle';
import { getInitials } from '@/shared/utils/string';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { SVGComponent } from '@/types';

const ICON_SCALE = 0.65;
const LOADER_SCALE = 0.4;
const ADD_PHOTO_SCALE = 0.45;
const EDIT_ICON_SIZE = 24;

const styles = StyleSheet.create({
  wrapper: { justifyContent: 'center', alignItems: 'center' },
  editIcon: { position: 'absolute', zIndex: 1, bottom: 0, right: 0 },
  disabled: { opacity: 0.6 }
});

export type ProfileAvatarProps = {
  mediaItem?: ClientMediaItem;
  mediaSize?: MediaItemSize;
  localImageUri?: string;
  textFallback?: AvatarTextFallback;
  iconFallback?: SVGComponent;
  viewSize: number;
  backgroundColor?: string;
  fallbackColor?: string;
  borderWidth?: number;
  borderColor?: string;
  isLoading?: boolean;
  /** Disables the edit affordance and dims the avatar. */
  disabled?: boolean;
  onEdit?: (canDelete: boolean) => void;
};

/**
 * Generic profile avatar component for persons, groups, organizations, ...
 * If possible, prefer the use of a more specialized component, such as {@link PersonAvatar}, over this one.
 */
export const ProfileAvatar: FC<ProfileAvatarProps> = ({
  mediaItem,
  localImageUri,
  textFallback,
  iconFallback,
  viewSize,
  backgroundColor,
  fallbackColor,
  borderWidth = 0,
  borderColor,
  mediaSize = MediaItemSize.THUMBNAIL,
  isLoading = false,
  disabled = false,
  onEdit
}) => {
  const theme = useAppTheme();

  const hasImage = localImageUri || mediaItem;
  const bgColor = backgroundColor ?? theme.colors.primary;
  const fbColor = fallbackColor ?? theme.colors.onPrimary;

  const loading = (
    <Avatar.Icon
      size={viewSize}
      style={{ backgroundColor: bgColor }}
      icon={() => (
        <ActivityIndicator
          size={Math.ceil(viewSize * LOADER_SCALE)}
          color={theme.colors.onPrimary}
        />
      )}
    />
  );

  const fallback = iconFallback ? (
    <Avatar.Icon
      size={viewSize}
      style={{ backgroundColor: bgColor }}
      icon={() =>
        iconFallback({
          fill: fbColor,
          width: Math.round(viewSize * ICON_SCALE),
          height: Math.round(viewSize * ICON_SCALE)
        })
      }
    />
  ) : (
    <Avatar.Text
      size={viewSize}
      color={fbColor}
      style={{ backgroundColor: bgColor }}
      label={
        textFallback?.shortName ??
        getInitials(textFallback?.firstName, textFallback?.lastName)
      }
    />
  );

  const addPhoto = (
    <IconButton
      style={[
        { backgroundColor: bgColor, padding: 0, margin: 0 },
        circleSize(viewSize)
      ]}
      onPress={() => onEdit?.(false)}
      icon={() => (
        <AddAPhoto
          fill={theme.colors.onPrimary}
          width={Math.round(viewSize * ADD_PHOTO_SCALE)}
          height={Math.round(viewSize * ADD_PHOTO_SCALE)}
        />
      )}
    />
  );

  const image = (
    <MediaItemImage
      mediaItem={mediaItem}
      mediaSize={mediaSize}
      localImageUri={localImageUri}
      style={circleSize(viewSize)}
    />
  );

  // Keep the contained edit button solid when disabled — paper's default
  // disabled colors are translucent, which looks washed out on top of the
  // already-dimmed avatar. Partial override only; the library deep-merges it
  // (and memoizes the merge keyed on this object's identity, so keep it
  // stable via useMemo).
  const editButtonTheme = useMemo(
    () => ({
      colors: {
        surfaceDisabled: theme.colors.primary,
        onSurfaceDisabled: theme.colors.onPrimary
      }
    }),
    [theme]
  );

  const renderContent = () => {
    if (isLoading) {
      return loading;
    }

    if (!hasImage) {
      return onEdit ? addPhoto : fallback;
    }

    return (
      <>
        {image}
        {onEdit && (
          <IconButton
            tabIndex={0}
            onPress={() => onEdit(true)}
            disabled={disabled}
            style={styles.editIcon}
            icon={() => (
              <EditIcon
                fill={theme.colors.onPrimary}
                width={EDIT_ICON_SIZE}
                height={EDIT_ICON_SIZE}
              />
            )}
            mode="contained"
            containerColor={theme.colors.primary}
            theme={editButtonTheme}
          />
        )}
      </>
    );
  };

  const outerSize = viewSize + borderWidth;
  const hasBorder = borderWidth > 0;

  // Todo: fix focus effect on browser
  return (
    <View
      style={[
        styles.wrapper,
        circleSize(outerSize),
        hasBorder && {
          backgroundColor: borderColor ?? theme.colors.background
        }
      ]}
    >
      <View
        style={[
          styles.wrapper,
          circleSize(viewSize),
          disabled && styles.disabled
        ]}
      >
        {renderContent()}
      </View>
    </View>
  );
};
